
import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PersonalConstants } from 'app/common/personalConstants';
import { ClaimsFnolService } from 'app/shared/services/claims-fnol.service';
import { FormErrorMapperService } from 'app/shared/services/form-error-mapper.service';
import { LoggerService } from 'app/shared/services/logger.service';
import { SessionService } from 'app/shared/services/session.service';

@Component({
    selector: 'is-tell-us-more-modal',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, FormsModule],
    providers: [FormErrorMapperService],
    templateUrl: './tell-us-more-modal.component.html',
    styleUrls: ['./tell-us-more-modal.component.scss'],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class TellUsMoreModalComponent implements OnInit {
    private claimNumber: string;
    private policyNumber: string;
    private userType: string;
    @ViewChild('tellUsMoreModal', { static: true }) tellUsMoreModal: ElementRef;
    @Output() closeAction = new EventEmitter<'dismiss' | 'confirm'>();

    tellUsMoreForm: UntypedFormGroup;
    apiError: boolean = false;

    constructor (
        private readonly fb: UntypedFormBuilder,
        private readonly claimsFnolService: ClaimsFnolService,
        private readonly sessionService: SessionService,
        private readonly logger: LoggerService,
        public formError: FormErrorMapperService
        ) {
    }

    ngOnInit(): void {
        this.initializeSessionData();
        this.tellUsMoreForm = this.fb.group({
            note: new FormControl('', [
                Validators.required,
                Validators.pattern(PersonalConstants.regexValidators.disallowOnlySpaces)
            ])
        });
    }

    private initializeLogger(): void {
        this.logger.setComponentName('TellUsMoreModalComponent');
        this.logger.setPageName('Tell Us More Modal');
        this.logger.info('TellUsMoreModalComponent - Component Initialized', {
            claimNumber: this.claimNumber,
            policyNumber: this.policyNumber,
            userType: this.userType
        });
    }

    private initializeSessionData(): void {
        this.claimNumber = this.sessionService.getClaimNumber();
        this.policyNumber = this.sessionService.getPolicyNumber();
        this.userType = this.sessionService.getUserType();
    }

    open(): void {
        this.initializeLogger();
        this.apiError = false;
        this.tellUsMoreForm.reset();
        this.tellUsMoreModal.nativeElement.openModal();
    }

    close(): void {
        this.apiError = false;
        this.tellUsMoreForm.reset();
        this.closeAction.emit('dismiss');
        this.tellUsMoreModal.nativeElement.closeModal();
    }

    submit(): void {
        this.apiError = false;
        const noteControl = this.tellUsMoreForm.get('note') as FormControl;
        const noteText = (noteControl?.value ?? '');
        const claimNumber = this.sessionService.getClaimNumber();
        if (!claimNumber) {
            this.apiError = true;
            this.logger.error('Claim number not found in session storage');
            return;
        }
        const noteObj = {
            body: noteText,
            topic: 'nw_customer_input',
            subject: 'Tell Us More'
        };
        this.claimsFnolService.buildCreateNoteRequest(noteObj, claimNumber).subscribe({
            next: (res: any) => {
                const code = res?.code;
                if (code !== undefined && code >= 400 && code < 600) {
                    this.apiError = true;
                    this.logger.info('Tell us more note creation returned error status', res);
                    return;
                }

                this.logger.info('Tell us more note created', noteObj);
                this.closeAction.emit('confirm');
                this.tellUsMoreModal.nativeElement.closeModal();
            },
            error: (err: any) => {
                this.apiError = true;
                this.closeAction.emit('confirm');
                this.logger.error('Error creating Tell us more note', err);
            }
        });
    }
}
